/**
 * The subset of a venue payload that carries imagery.
 *
 * Declared structurally rather than importing a concrete type because the two
 * venue shapes in play — `venueConfigParser` (the `/book` route) and
 * `bookingParser.venue` (`/place-your-order`, `/manage-your-order`) — are
 * separate schemas that happen to name this field identically.
 */
export interface VenueImageSource {
  photo?: string | null;
}

/**
 * Resolves the venue image to display in the booking sidebar banner.
 *
 * Collins builds `photo` from the first entry of the venue's own `photos`
 * array, at the 620px `medium` size, falling back to the full-size original
 * when the upload was too small for a medium variant to exist. There is no
 * fallback to the venue's email logo: that asset is 140px and intended for
 * email headers, so stretching it across a 200px-tall cover banner looks
 * broken. A venue with no photo simply gets no banner.
 *
 * Collins returns an empty string for "no image" on some venues, and
 * `transformBookingResponse` substitutes `photo: false` for pending bookings,
 * so anything that is not a non-blank string is treated as absent.
 *
 * The returned URL is used verbatim. Collins serves these from its own CDN and
 * may return protocol-relative URLs (e.g. `//static.designmynight.com/...`), so
 * the result must never be passed through `assetUrl()` — that would prepend the
 * Guest Checkout origin and break the request.
 */
export function resolveVenueImageUrl(venue: VenueImageSource | null | undefined): string | undefined {
  const photo = venue?.photo;

  return typeof photo === "string" && photo.trim() !== "" ? photo : undefined;
}
